import {forwardRef, useEffect, useMemo, useRef, useState} from 'react'
import {
  defaultPhoneCountry,
  capPhoneDigits,
  formatPhone,
  internationalPhone,
  nationalPhoneDigits,
  phoneCountries,
  phoneCountryForValue,
  phoneValidationMessage,
  PhoneCountry,
} from '../../tenant/phoneValidation'
import './TenantPhoneInput.scss'

type Props = {
  id?: string
  value: string
  onChange: (value: string) => void
  onBlur?: () => void
  language?: string
  required?: boolean
  error?: string
  className?: string
  placeholder?: string
  onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void
}

const flag = (iso: string) => /^[A-Za-z]{2}$/.test(iso)
  ? iso.toUpperCase().replace(/./g, (letter) => String.fromCodePoint(127397 + letter.charCodeAt(0)))
  : ''

const TenantPhoneInput = forwardRef<HTMLInputElement, Props>(({
  id, value, onChange, onBlur, language = 'en', required = true, error, className = '', placeholder, onKeyDown,
}, ref) => {
  const [country, setCountry] = useState<PhoneCountry>(() => value ? phoneCountryForValue(value, true) : defaultPhoneCountry())
  const [display, setDisplay] = useState(() => formatPhone(value, value ? phoneCountryForValue(value, true) : defaultPhoneCountry(), true))
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [touched, setTouched] = useState(false)
  const root = useRef<HTMLDivElement>(null)
  const countries = useMemo(phoneCountries, [])
  const inlineError = touched ? phoneValidationMessage(display, country, language, required) : ''

  useEffect(() => {
    const close = (event: MouseEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [])

  useEffect(() => {
    const nextCountry = value ? phoneCountryForValue(value, true) : defaultPhoneCountry()
    setCountry(nextCountry)
    setDisplay(formatPhone(value, nextCountry, true))
  }, [value])

  const chooseCountry = (next: PhoneCountry) => {
    const national = nationalPhoneDigits(display, country)
    const hasValue = national !== ''
    setCountry(next)
    setDisplay(hasValue ? formatPhone(national, next) : '')
    onChange(hasValue ? `+${next.dial_code}${national.replace(/^0+/, '')}` : '')
    setOpen(false)
    setQuery('')
  }

  const changeNumber = (raw: string) => {
    const capped = capPhoneDigits(raw, country)
    setDisplay(formatPhone(capped, country))
    onChange(internationalPhone(capped, country))
    setTouched(true)
  }

  const search = query.trim().toLowerCase()
  const filtered = countries.filter((item) => {
    const name = (language === 'ar' ? item.country_ar || item.country : item.country).toLowerCase()
    return !search || name.startsWith(search) || item.dial_code.startsWith(search.replace(/^\+/, ''))
  })

  return (
    <div
      className='tenant-phone-field'
      ref={root}
      onKeyDown={(event) => {
        if (event.key === 'Escape' && open) {
          event.stopPropagation()
          setOpen(false)
        }
      }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) {
          setTouched(true)
          onBlur?.()
        }
      }}
    >
      <div className={`tenant-phone-input ${inlineError || error ? 'tenant-phone-input--invalid' : ''}`}>
        <button type='button' className='tenant-phone-input__country' onClick={() => setOpen(!open)} aria-expanded={open} title={country.country}>
          <span aria-hidden='true'>{flag(country.iso_code_2)}</span>
          <span dir='ltr'>+{country.dial_code}</span>
          <span className='tenant-phone-input__arrow' aria-hidden='true'>⌄</span>
        </button>
        <input
          ref={ref}
          id={id}
          type='tel'
          inputMode='numeric'
          autoComplete='tel-national'
          className={`tenant-phone-input__number ${className}`}
          value={display}
          placeholder={placeholder}
          onChange={(event) => changeNumber(event.target.value)}
          onKeyDown={onKeyDown}
        />
        {open && (
          <div className='tenant-phone-input__dropdown'>
            <input
              autoFocus
              className='tenant-phone-input__search'
              type='search'
              value={query}
              placeholder={language === 'ar' ? 'ابحث عن الدولة أو الرمز' : 'Search country or code'}
              onChange={(event) => setQuery(event.target.value)}
            />
            <div className='tenant-phone-input__countries' role='listbox'>
              {filtered.map((item) => (
                <button type='button' role='option' aria-selected={item.iso_code_2 === country.iso_code_2} key={item.iso_code_2} onClick={() => chooseCountry(item)}>
                  <span aria-hidden='true'>{flag(item.iso_code_2)}</span>
                  <span>{language === 'ar' ? item.country_ar || item.country : item.country}</span>
                  <span dir='ltr'>+{item.dial_code}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      {(inlineError || error) && <div className='tenant-phone-input__error'>{inlineError || error}</div>}
    </div>
  )
})

TenantPhoneInput.displayName = 'TenantPhoneInput'

export default TenantPhoneInput
